import preferences from "@coworker/enums/profilePreferences";
import { useMemo } from "react";
import type { ApplyFilter } from "../../shared/filters/types/apply";
import type {
  FilterValue,
  InputFilter,
} from "../../shared/filters/types/filter";
import { useMemoryFilteredData } from "../../shared/filters/utils/apply/useMemoryFilteredData";
import { useUserPreferenceStoreFilterValues } from "../../shared/filters/utils/store/useUserPreferenceStoreFilterValues";
import type { Launch } from "../types/launch";
import {
  flattenRows,
  groupRowsByLaunch,
  type LaunchMediaRow,
  type MediasByLaunchId,
} from "../utils/launchMediaFilters";
import {
  createFiscalYearAnyFilter,
  useFiscalYearCheckboxFilters,
} from "./filters/useFiscalYearCheckboxFilters";
import { useHfbMultiSelectLaunchMediaFilter } from "./filters/useHfbMultiSelectLaunchMediaFilter";
import { useLaunchMultiSelectFilter } from "./filters/useLaunchMultiSelectFilter";
import { useMediaTypeMultiSelectFilter } from "./filters/useMediaTypeMultiSelectFilter";

function useBaseFilters(launches: Launch[], rows: LaunchMediaRow[]) {
  const medias = useMemo(() => rows.map((r) => r.media), [rows]);
  return {
    yearFilters: useFiscalYearCheckboxFilters(launches),
    mediaTypeFilter: useMediaTypeMultiSelectFilter(medias),
    hfbFilter: useHfbMultiSelectLaunchMediaFilter(medias),
    launchFilter: useLaunchMultiSelectFilter(launches),
  };
}

export function useLaunchMediaFilters(
  launches: Launch[],
  mediasByLaunchId: MediasByLaunchId
) {
  const { filterValues, setFilterValues } = useUserPreferenceStoreFilterValues(
    preferences.LAUNCH_MEDIA_FILTERS
  );
  const rows = useMemo(
    () => flattenRows(launches, mediasByLaunchId),
    [launches, mediasByLaunchId]
  );

  const base = useBaseFilters(launches, rows);

  const useFiltersFactory = (_values: FilterValue[]) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const f = useBaseFilters(launches, rows);
    return [
      ...f.yearFilters,
      f.mediaTypeFilter.filter,
      f.hfbFilter.filter,
      f.launchFilter.filter,
    ];
  };

  const dataFilters: ApplyFilter<InputFilter, LaunchMediaRow>[] = [
    createFiscalYearAnyFilter(base.yearFilters, filterValues),
    base.mediaTypeFilter,
    base.hfbFilter,
    base.launchFilter,
  ];

  const filteredRows = useMemoryFilteredData<LaunchMediaRow>(
    // useMemoryFilteredData sorts in place, so give it a copy
    [...rows],
    dataFilters,
    filterValues
  );

  const sections = useMemo(
    () => groupRowsByLaunch(launches, filteredRows ?? []),
    [launches, filteredRows]
  );

  return { useFiltersFactory, filterValues, setFilterValues, sections };
}
